import { Gift } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { CustomerDetail } from '@/lib/api/types'
import { formatDate, formatUsd } from '@/lib/format'
import { useGrantTopUp } from './api'

const STATUS = { active: 'Active', used_up: 'Used up', refunded: 'Refunded' } as const

/**
 * The customer's top-ups (one-time packs of a monthly allowance), bought or given by staff.
 * Owners and admins can give some, for a goodwill gesture or a support case; the customer is told.
 */
export function TopUpsCard({ customer: c, canManage }: { customer: CustomerDetail; canManage: boolean }) {
  const [open, setOpen] = useState(false)
  const topUps = c.top_ups
  if (!topUps) return null

  return (
    <Card>
      <CardHeader
        title="Top-ups"
        description="One-time packs, used after the month’s allowance. Nothing renews and nothing expires."
        actions={
          canManage &&
          c.closed_at === null && (
            <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
              <Gift /> Give a top-up
            </Button>
          )
        }
      />
      <CardBody>
        {topUps.packs.length === 0 ? (
          <p className="text-[13px] text-ink-subtle">None yet.</p>
        ) : (
          <ul className="grid divide-y divide-border text-[13px]" aria-label="Top-ups">
            {topUps.packs.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="grid gap-0.5">
                  <span className="font-medium text-ink">
                    {p.units.toLocaleString()} {p.name}
                  </span>
                  <span className="text-xs text-ink-subtle">
                    {p.source === 'purchase'
                      ? `Bought for ${formatUsd(p.price_paid)}`
                      : `Given by ${p.granted_by ?? 'former staff'}${p.note ? `: ${p.note}` : ''}`}
                    {p.purchased_at && ` · ${formatDate(p.purchased_at)}`}
                  </span>
                </span>
                <span className="text-xs text-ink-muted">
                  {STATUS[p.status]} · {p.remaining.toLocaleString()} left
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
      {open && <GrantDialog customer={c} onClose={() => setOpen(false)} />}
    </Card>
  )
}

function GrantDialog({ customer: c, onClose }: { customer: CustomerDetail; onClose: () => void }) {
  const catalog = c.top_ups?.catalog ?? []
  const grant = useGrantTopUp(c.id)
  const [pack, setPack] = useState(catalog[0]?.code ?? '')
  const [quantity, setQuantity] = useState('1')
  const [note, setNote] = useState('')
  const chosen = catalog.find((p) => p.code === pack)
  const qty = Number(quantity)
  const fieldError = (f: string) => (isApiError(grant.error) ? grant.error.firstFieldError(f) : undefined)

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Give a top-up"
        description={`It’s added to ${c.name} at once and the owner is told. It never expires and costs us its data, so give it for a reason.`}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              loading={grant.isPending}
              disabled={!chosen || !(qty >= 1 && qty <= 10) || note.trim() === ''}
              onClick={() =>
                grant.mutate(
                  { pack, quantity: qty, note: note.trim() },
                  {
                    onSuccess: () => {
                      toast.success('Top-up added.')
                      onClose()
                    },
                    onError: (e) => toast.error(errorMessage(e)),
                  },
                )
              }
            >
              Give top-up
            </Button>
          </>
        }
      >
        <div className="grid gap-4">
          <Field label="Top-up" htmlFor="top-up-pack" required error={fieldError('pack')}>
            <NativeSelect id="top-up-pack" value={pack} onChange={(e) => setPack(e.target.value)}>
              {catalog.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name} (+{p.units.toLocaleString()})
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Packs" htmlFor="top-up-quantity" required error={fieldError('quantity')}>
            <Input
              id="top-up-quantity"
              type="number"
              min={1}
              max={10}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </Field>
          <Field
            label="Reason"
            htmlFor="top-up-note"
            required
            error={fieldError('note')}
            hint="Shown to staff, not the customer."
          >
            <Input id="top-up-note" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {chosen && qty >= 1 && (
            <p className="text-[13px] text-ink-muted">
              Adds {(chosen.units * qty).toLocaleString()} {chosen.name}, worth{' '}
              {formatUsd(chosen.price * qty)}.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
